import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { environment } from 'src/environments/environment';

export interface CalculatorCountry {
  code: string;
  name: string;
  currency: string;
  flagIcon: string | null;
  displayOrder: number;
}

export interface CalculatorVariant {
  code: string;
  baseCountry: string;
  label: string;
  sellPriceUsd: number;
  fixedCost: number;
  fixedCostUsd: number;
  destinationCurrency: string;
  minAmountUsd: number;
  businessDays: number;
  validUntil: string | null;
}

/**
 * Regla de visibilidad que Vita publica tanto a nivel de field (`when`) como
 * a nivel de option (`visible`). El `value` puede venir como string o como
 * array de strings — cuando es array, el match es "value ∈ lista".
 */
export interface CalculatorVisibilityRule {
  key: string;
  value: string | string[];
}

export interface CalculatorFormOption {
  value: string | number;
  label: string;
  /** Vita decide si esta option aparece según otros campos del form. */
  visible?: CalculatorVisibilityRule;
  /** Código interno de Vita (ej. transferswap_code de bancos VE). Pass-through. */
  transferswap_code?: string;
}

export interface CalculatorFormField {
  key: string;
  name: string;
  type: 'text' | 'email' | 'select' | string;
  min?: number;
  max?: number;
  options?: CalculatorFormOption[];
  when?: CalculatorVisibilityRule;
}

export interface CalculatorFormSchema {
  variant: string;
  fields: CalculatorFormField[];
  inheritedFrom?: string;
}

/**
 * Decide si una `key` del schema de Vita corresponde a un field del
 * BENEFICIARIO (debe mostrarse en el form de registro y en el resumen)
 * o a data del EMISOR / contexto del envío (no se pide al guardar):
 *  - `fc_*`  → Final Customer KYC del cliente que envía (va con la tx).
 *  - `purpose` / `purpose_comentary` → motivo del envío (cambia por tx).
 *  - `beneficiary_type` → Persona Natural/Business; se auto-setea y oculta.
 */
export function isBeneficiaryField(key: string): boolean {
  if (!key) return false;
  if (key.startsWith('fc_')) return false;
  if (key === 'purpose' || key === 'purpose_comentary') return false;
  if (key === 'beneficiary_type') return false;
  return true;
}

export interface CalculatorEstimateResult {
  countryCode: string;
  variant: string;
  variantLabel: string;
  sourceCurrency: 'USD';
  destinationCurrency: string;
  amountSent: number;
  sellPrice: number;
  fixedCost: number;
  received: number;
  minAmountUsd: number;
  businessDays: number;
  validUntil: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class VitaCalculatorService {
  private baseUrl = `${environment.apiNest}vita-wallet/calculator`;

  constructor(
    private http: HttpClient,
    private cognitoService: CognitoService,
  ) {}

  private async getHeaders(): Promise<HttpHeaders> {
    // idToken (NO accessToken): backend valida `tokenUse: 'id'` — espejo del
    // ApiRestInterceptor. accessToken → 401 "Sesión expirada" en bucle.
    let session = await this.cognitoService.getSession();
    let token = session?.idToken?.jwtToken;

    if (token && this.isJwtNearExpiry(token, 60)) {
      if (typeof (this.cognitoService as any).clearCache === 'function') {
        (this.cognitoService as any).clearCache();
      }
      session = await this.cognitoService.getSession();
      token = session?.idToken?.jwtToken;
    }

    if (!token) {
      throw new Error('No se pudo obtener el token de sesión');
    }
    return new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Authorization', `Bearer ${token.trim()}`);
  }

  private isJwtNearExpiry(jwt: string, thresholdSec: number): boolean {
    try {
      const parts = jwt.split('.');
      if (parts.length !== 3) return false;
      const payload = JSON.parse(
        atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')),
      );
      if (!payload?.exp) return false;
      const nowSec = Math.floor(Date.now() / 1000);
      return payload.exp - nowSec < thresholdSec;
    } catch {
      return false;
    }
  }

  private handleError(error: HttpErrorResponse) {
    const msg =
      error.error?.message ||
      (typeof error.error === 'string' ? error.error : '') ||
      `Error del servidor: ${error.status}`;
    return throwError(() => msg);
  }

  listCountries(): Observable<CalculatorCountry[]> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.get<CalculatorCountry[]>(`${this.baseUrl}/countries`, { headers }),
      ),
      catchError(this.handleError),
    );
  }

  getVariants(countryCode: string): Observable<CalculatorVariant[]> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.get<CalculatorVariant[]>(
          `${this.baseUrl}/variants/${encodeURIComponent(countryCode)}`,
          { headers },
        ),
      ),
      catchError(this.handleError),
    );
  }

  getRules(variant: string): Observable<CalculatorFormSchema> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.get<CalculatorFormSchema>(
          `${this.baseUrl}/rules/${encodeURIComponent(variant)}`,
          { headers },
        ),
      ),
      catchError(this.handleError),
    );
  }

  estimate(body: {
    countryCode: string;
    variant?: string;
    amountUsd: number;
  }): Observable<CalculatorEstimateResult> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.post<CalculatorEstimateResult>(
          `${this.baseUrl}/estimate`,
          body,
          { headers },
        ),
      ),
      catchError(this.handleError),
    );
  }
}
